import { z } from 'zod';

import { SignupFieldEntrySchema } from './signup-custom-fields.schema';
import { isValidPhoneNumber } from '@kit/ui/phone-input';

import { FullNameSchema } from './full-name.schema';
import { RefinedPasswordSchema, refineRepeatPassword } from './password.schema';

const SPECIAL_CHARACTERS_REGEX = /[!@#$%^&*()+=[\]{};':"\\|,.<>/?]/;
const RESERVED_NAMES_ARRAY = ['settings', 'billing', 'admin', 'home', 'api', 'auth'];

export const EntityNameSchema = z
  .string()
  .min(2, 'Entity name must be at least 2 characters')
  .max(50, 'Entity name must be at most 50 characters')
  .refine((name) => !SPECIAL_CHARACTERS_REGEX.test(name), {
    message: 'Entity name cannot contain special characters',
  })
  .refine((name) => !RESERVED_NAMES_ARRAY.includes(name.toLowerCase()), {
    message: 'This name is reserved and cannot be used',
  });

// Child account schema for parent sign-up flow
// Children are created as guest accounts with synthetic emails (parent+childname@domain.com)
// This allows parents to book sessions on behalf of their children
export const ChildSchema = z.object({
  firstName: z.string().min(2, 'First name must be at least 2 characters').max(50),
  lastName: z.string().min(2, 'Last name must be at least 2 characters').max(50),
});

export const PasswordSignUpSchema = z
  .object({
    signupFields: z.array(SignupFieldEntrySchema).optional(),
    name: FullNameSchema,
    email: z.string().email(),
    password: RefinedPasswordSchema,
    repeatPassword: RefinedPasswordSchema,
    // Required to COLLECT; only verification is optional (the skippable
    // WhatsApp verify step). The email stays the verified anchor here — the
    // typed phone is stored unconfirmed until proven.
    phoneNumber: z
      .string()
      .min(1, 'Phone number is required')
      .refine((value) => isValidPhoneNumber(value), {
        message:
          'Please enter a valid phone number with the correct number of digits',
      }),
    // Entity name is optional during regular sign-up, required only when creating business accounts
    // Transform empty strings to undefined for cleaner optional handling
    entityName: z.union([
      EntityNameSchema,
      z.literal(''),
    ]).optional().transform((val) => val === '' ? undefined : val),
    termsAccepted: z.boolean().optional(),
    // Children array is optional and limited to 5 to prevent abuse
    // Empty children (missing firstName/lastName) are filtered server-side
    children: z.array(ChildSchema).max(5).optional().default([]),
  })
  .superRefine(refineRepeatPassword);
